import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Map, StyleSpecification } from 'maplibre-gl';
import { map, of } from 'rxjs';
import { MapLayerOrderingService } from '@services/map/map-layer-ordering.service';
import { BackgroundStyle, MapStyleConfiguration } from '@services/map/abstract-map.service';

@Injectable({
  providedIn: 'root',
})
export class MapLayersApiService {
  readonly http = inject(HttpClient);
  readonly mapLayerOrderingService = inject(MapLayerOrderingService);

  switchMapBackground(mapObject: Map, mapStyle: MapStyleConfiguration) {
    if (!this.isBackgroundLoaded(mapObject, mapStyle)) {
      this.addLayersFromApi(mapObject, mapStyle).subscribe(() => {
        this.setBackgroundVisibilities(mapStyle, mapObject);
      });
    } else {
      this.setBackgroundVisibilities(mapStyle, mapObject);
    }
  }

  private isBackgroundLoaded(mapObject: Map, mapStyle: MapStyleConfiguration) {
    return mapObject.getStyle()?.layers.some((layer) => layer.id.startsWith(mapStyle.id));
  }

  private setBackgroundVisibilities(mapStyle: MapStyleConfiguration, mapObject: Map) {
    for (let obj of Object.values(BackgroundStyle)) {
      this.setBackgroundVisibility(mapObject, obj.id, obj.symbolsId, obj.id === mapStyle.id);
    }
  }

  private setBackgroundVisibility(mapObject: Map, id: string, symbolsId: string, visible: boolean) {
    mapObject.getStyle().layers.forEach((layer) => {
      if (layer.id.startsWith(id) || layer.id.startsWith(symbolsId)) {
        mapObject.setLayoutProperty(layer.id, 'visibility', visible ? 'visible' : 'none');
      }
    });
  }

  addLayersFromApi(mapObject: Map, mapStyle: MapStyleConfiguration) {
    const style$ =
      typeof mapStyle.style === 'string'
        ? this.http.get<StyleSpecification>(mapStyle.style)
        : of(JSON.parse(JSON.stringify(mapStyle.style)) as StyleSpecification);

    return style$.pipe(
      map((style) => {
        Object.entries(style.sources).forEach(([sourceKey, source]) => {
          // check if source already exists
          const sourceId = mapStyle.id + '-' + sourceKey;
          if (!mapObject.getSource(sourceId)) {
            mapObject.addSource(sourceId, source);
          }
        });

        const addedLayerIds = [] as string[];
        style.layers.forEach((layer) => {
          // Don't add POI layers to the map
          if (
            mapStyle.ignoreLayersWithPrefix &&
            mapStyle.ignoreLayersWithPrefix.some((prefix) => layer.id.startsWith(prefix))
          ) {
            return;
          }
          // Ignore layers that are default invisible, unless this style opts to force them visible.
          if (layer.layout?.visibility === 'none') {
            if (!mapStyle.forceVisibleLayerIds?.includes(layer.id)) {
              return;
            }
            layer.layout = { ...layer.layout, visibility: 'visible' };
          }
          const styleId = layer.type === 'symbol' ? mapStyle.symbolsId : mapStyle.id;

          layer.id = styleId + '-' + layer.id;
          // @ts-ignore
          layer.source = mapStyle.id + '-' + layer.source;

          const beforeId = this.mapLayerOrderingService.getLayerBeforeId(styleId, mapObject);

          if (mapObject.getLayer(layer.id)) {
            return;
          }
          mapObject?.addLayer(layer, beforeId);

          addedLayerIds.push(layer.id);
        });

        return addedLayerIds;
      }),
    );
  }
}
